/**
 * Green Flag — InviteSparkAfterRegisterDialog
 *
 * Offered the moment someone finishes registering for an event: the people
 * they've already Sparked with are exactly who they'd want there, and this is
 * when they're most likely to ask. Without it the invite lives behind the
 * "Invite a Spark" button on the confirmation card, which is easy to walk past.
 *
 * The dialog only appears when there is actually someone to invite. It loads
 * the viewer's mutual pre-event Sparks and narrows them with
 * `invitableSparksForEvent` — no outstanding invite, not already going, and
 * eligible for this event. If nothing survives that (or the list can't be
 * loaded), nothing is shown and `onNothingToInvite` fires so the caller can
 * move straight on to whatever came next — on the event page, the
 * complete-your-profile prompt.
 *
 * One tap sends. There's no confirm-the-event step because there's no choice
 * left to make: the event is the one they just registered for, and everyone
 * on the list has already been checked against it, so a confirmation screen
 * would only ask them to re-approve what they just picked. The list copy says
 * the tap sends, and the outcome lands on the confirmation screen shared with
 * the event page's invite drawer.
 */

import {
  Box,
  Button,
  DialogBackdrop,
  DialogContent,
  DialogPositioner,
  DialogRoot,
  Flex,
  Heading,
  Text,
} from "@chakra-ui/react";
import {
  MatchesPageData,
  MatchesPageSpark,
  SparkWithRegistrationSummary,
} from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { useEffect, useMemo, useRef, useState } from "react";
import { apiReq } from "../../lib/apiReq";
import { toaster } from "../../lib/toaster";
import { Icon } from "../Icon/Icon";
import {
  InviteSentConfirmation,
  SparkPickerRow,
} from "../InviteSparkFlow/InviteSparkPrimitives";
import {
  invitableSparksForEvent,
  inviteErrorMessage,
} from "../InviteSparkFlow/inviteSparkShared";

export interface InviteSparkAfterRegisterDialogProps {
  /** True once the viewer has registered and the prompt is due. */
  open: boolean;
  /** The event they just registered for. Null until it has loaded. */
  spark: SparkWithRegistrationSummary | null;
  /** The viewer dismissed the prompt, or finished sending an invite. */
  onClose: () => void;
  /**
   * There was nobody to invite, so nothing was rendered. Fires at most once
   * per open — the caller uses it to continue as if the prompt never existed.
   */
  onNothingToInvite: () => void;
}

export const InviteSparkAfterRegisterDialog = ({
  open,
  spark,
  onClose,
  onNothingToInvite,
}: InviteSparkAfterRegisterDialogProps) => {
  // The viewer's mutual pre-event sparks as of this open — null while unknown.
  // Held here rather than read off the request state so a previous open's
  // result can never be mistaken for this one's.
  const [sparks, setSparks] = useState<MatchesPageSpark[] | null>(null);
  // Who we're mid-send to, and who the invite landed with. Only one of these
  // is ever set: pending drives the row spinner, sent swaps in the receipt.
  const [pending, setPending] = useState<MatchesPageSpark | null>(null);
  const [sent, setSent] = useState<MatchesPageSpark | null>(null);
  const [matchesReq] = useReq<MatchesPageData>(apiReq);
  const [inviteReq] = useReq(apiReq);

  // One fetch per open, guarded on a ref: `spark` gets a new identity every
  // time the page refetches the event, and without the guard each of those
  // would fire another request. No retry affordance on purpose — this prompt
  // is a bonus, so a failed load resolves as "nobody to invite" rather than
  // putting an error in front of someone who just registered.
  const requestedRef = useRef(false);
  useEffect(() => {
    if (!open || !spark) {
      requestedRef.current = false;
      setSparks(null);
      return;
    }
    if (requestedRef.current) return;
    requestedRef.current = true;
    void matchesReq.get("/user/matches-page").then((response) => {
      setSparks(response.success ? response.result.sparks : []);
    });
  }, [open, spark, matchesReq]);

  const invitable = useMemo<MatchesPageSpark[] | null>(
    () => (spark && sparks ? invitableSparksForEvent(sparks, spark) : null),
    [spark, sparks],
  );

  // Hand control back the moment we know there's nobody to ask about, so the
  // caller isn't left waiting on a dialog that will never render. Latched so a
  // re-render can't fire it twice.
  const resolvedEmptyRef = useRef(false);
  useEffect(() => {
    if (!open) {
      resolvedEmptyRef.current = false;
      return;
    }
    if (resolvedEmptyRef.current) return;
    if (invitable && invitable.length === 0) {
      resolvedEmptyRef.current = true;
      onNothingToInvite();
    }
  }, [open, invitable, onNothingToInvite]);

  const close = () => {
    setPending(null);
    setSent(null);
    onClose();
  };

  /**
   * Picking someone IS the send. Everyone on this list already passed the
   * event's age/capacity rules and has no invite outstanding, so the only
   * thing a confirmation step could add is a second tap. A failure keeps them
   * on the list with the reason in a toast, so they can try someone else.
   */
  const invite = async (target: MatchesPageSpark) => {
    if (!spark || pending) return;
    setPending(target);
    const response = await inviteReq.post("/user/spark-invitations", {
      invitedUserId: target.profile.id,
      sparkId: spark.id,
    });
    setPending(null);
    if (response.success) {
      setSent(target);
    } else {
      toaster.create({
        type: "error",
        title: inviteErrorMessage(
          response.error?.code,
          target.profile.firstName || "them",
        ),
      });
    }
  };

  const visible = open && !!spark && !!invitable && invitable.length > 0;

  return (
    <DialogRoot
      open={visible}
      onOpenChange={(d) => {
        // Don't let a backdrop tap yank the dialog out from under an invite
        // that's already on the wire — the viewer would never learn how it went.
        if (!d.open && !pending) close();
      }}
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent
          maxW="25rem"
          w="calc(100% - 2rem)"
          mx="auto"
          my="2rem"
          maxH="calc(100dvh - 4rem)"
          borderRadius="xl"
          bg="bg.canvas"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Box flex="1" minH="0" overflowY="auto" padding="1rem">
            {sent ? (
              <InviteSentConfirmation
                firstName={sent.profile.firstName || "them"}
                onDone={close}
              />
            ) : (
              <>
                <Flex
                  direction="column"
                  align="center"
                  gap="0.75rem"
                  textAlign="center"
                  paddingBottom="1rem"
                >
                  <Flex
                    align="center"
                    justify="center"
                    boxSize="3rem"
                    borderRadius="full"
                    bg="bg.brand"
                    color="fg.onBrand"
                  >
                    <Icon name="spark-filled" boxSize="1.5rem" />
                  </Flex>
                  <Heading as="h3" textStyle="headingSm">
                    Invite a Spark to Join You
                  </Heading>
                  <Text fontSize="1rem" color="fg.muted">
                    You're going to {spark?.title || spark?.activity}. Tap
                    someone to send them an invite.
                  </Text>
                </Flex>
                <Flex direction="column" gap="0.5rem">
                  {invitable?.map((s) => (
                    <SparkPickerRow
                      key={s.profile.id}
                      spark={s}
                      loading={pending?.profile.id === s.profile.id}
                      disabled={!!pending}
                      onPick={() => void invite(s)}
                    />
                  ))}
                </Flex>
              </>
            )}
          </Box>

          {!sent && (
            <Box
              position="sticky"
              bottom={0}
              bg="bg.canvas"
              borderTop="1px solid"
              borderColor="border.subtle"
              padding="1rem"
            >
              <Button
                variant="ghost"
                width="100%"
                color="fg.muted"
                disabled={!!pending}
                onClick={close}
              >
                Maybe later
              </Button>
            </Box>
          )}
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};
